import React from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

// TableCard — the standard table shell shared across list pages. A bordered card with an
// integrated header (title + subtitle, an optional search box, an optional segmented status
// filter, and an optional primary action) and a body that renders standardized loading/error
// states, otherwise its children (typically an <EmptyState/> or a <FlexTable/>).
//
// controlsPlacement:
//   "second-row" (default) — search + filter sit on a second row below the subtitle, left-aligned
//                            (primary action stays top-right on the first row).
//   "first-row"            — search + filter + primary action all sit on the first row, right-aligned.
export default function TableCard({
	title,
	subtitle,
	primaryAction,
	searchValue,
	onSearchChange,
	searchPlaceholder = "Search…",
	searchWidth = 260,
	filterValue,
	onFilterChange,
	filterOptions,
	filterAriaLabel = "Filter",
	controls,
	controlsPlacement = "second-row",
	isLoading = false,
	error = null,
	errorMessage = "Something went wrong",
	onRetry,
	children,
}) {
	const hasSearch = typeof onSearchChange === "function";
	const hasFilter = Array.isArray(filterOptions) && filterOptions.length > 0;
	// Skip the whole header strip when nothing populates it (e.g. a page that moved
	// its title / search / actions into the app header).
	const hasHeader = title != null || subtitle != null || hasSearch || hasFilter || controls != null || primaryAction != null;
	const firstRow = controlsPlacement === "first-row";

	const searchEl = hasSearch ? (
		<div className="relative">
			<FontAwesomeIcon
				icon={icon({ name: "magnifying-glass" })}
				className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-text-3"
			/>
			<input
				type="text"
				value={searchValue}
				onChange={(e) => onSearchChange(e.target.value)}
				placeholder={searchPlaceholder}
				aria-label={searchPlaceholder}
				style={{ width: searchWidth }}
				className="rounded-ctrl border border-card-bd bg-card py-1.5 pl-8 pr-3 text-[13px] text-text placeholder:text-text-3 focus:outline-none focus:ring-1 focus:ring-primary"
			/>
		</div>
	) : null;

	const filterEl = hasFilter ? (
		<div
			aria-label={filterAriaLabel}
			className="inline-flex items-center rounded-seg border border-card-bd p-0.5"
		>
			{filterOptions.map((opt) => {
				const selected = filterValue === opt.value;
				return (
					<button
						key={opt.value}
						type="button"
						aria-pressed={selected}
						onClick={() => onFilterChange(opt.value)}
						className={`rounded-seg px-3 py-1 text-[13px] transition-colors ${
							selected
								? "bg-primary font-semibold text-white"
								: "text-text-2 hover:text-text"
						}`}
					>
						{opt.label}
					</button>
				);
			})}
		</div>
	) : null;

	const actionEl = primaryAction ? (
		<button
			type="button"
			onClick={primaryAction.onClick}
			disabled={primaryAction.disabled}
			className="inline-flex shrink-0 items-center gap-1.5 rounded-ctrl bg-primary px-3 py-1.5 text-[13px] font-medium text-white transition-colors hover:bg-primary-hover disabled:opacity-50"
		>
			{primaryAction.icon && <FontAwesomeIcon icon={primaryAction.icon} className="text-[13px]" />}
			{primaryAction.label}
		</button>
	) : null;

	return (
		<div className="rounded-card border border-card-bd bg-card">
			{hasHeader && (
			title == null ? (
				// Untitled layout: the controls (search + filters) sit on the first row,
				// with the count/subtitle below them on the second row.
				<div className="border-b border-card-bd px-4 py-3">
					<div className="flex flex-wrap items-center justify-between gap-3">
						<div className="flex flex-wrap items-center gap-3">
							{searchEl}
							{filterEl}
							{controls}
						</div>
						{actionEl}
					</div>
					{subtitle != null && <p className="mt-2 text-xs text-text-3">{subtitle}</p>}
				</div>
			) : (
				<div className="border-b border-card-bd px-4 py-3">
					<div className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<div className="text-sm font-semibold uppercase tracking-wide text-text-2">{title}</div>
							{subtitle != null && <p className="mt-0.5 text-xs text-text-3">{subtitle}</p>}
						</div>
						{firstRow ? (
							(searchEl || filterEl || controls || actionEl) && (
								<div className="flex flex-wrap items-center justify-end gap-3">
									{searchEl}
									{filterEl}
									{controls}
									{actionEl}
								</div>
							)
						) : (
							actionEl
						)}
					</div>

					{!firstRow && (hasSearch || hasFilter || controls) && (
						<div className="mt-3 flex flex-wrap items-center gap-3">
							{searchEl}
							{filterEl}
							{controls}
						</div>
					)}
				</div>
			)
			)}

			<div className="px-3 pb-3 pt-2">
				{isLoading ? (
					<div className="flex min-h-[240px] items-center justify-center">
						<div
							data-testid="table-card-spinner"
							className="h-[32px] w-[32px] animate-spin rounded-full border-b-2 border-text-2"
						/>
					</div>
				) : error ? (
					<div className="flex min-h-[240px] flex-col items-center justify-center">
						<div className="mb-2 text-danger">{errorMessage}</div>
						{onRetry && (
							<button
								type="button"
								onClick={onRetry}
								className="rounded-ctrl bg-primary px-3 py-1.5 text-[13px] font-medium text-white transition-colors hover:bg-primary-hover"
							>
								Retry
							</button>
						)}
					</div>
				) : (
					children
				)}
			</div>
		</div>
	);
}

TableCard.propTypes = {
	title: PropTypes.node,
	subtitle: PropTypes.node,
	primaryAction: PropTypes.shape({
		label: PropTypes.node.isRequired,
		onClick: PropTypes.func.isRequired,
		icon: PropTypes.object,
		disabled: PropTypes.bool,
	}),
	searchValue: PropTypes.string,
	onSearchChange: PropTypes.func,
	searchPlaceholder: PropTypes.string,
	searchWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	filterValue: PropTypes.string,
	onFilterChange: PropTypes.func,
	filterOptions: PropTypes.arrayOf(
		PropTypes.shape({ value: PropTypes.string, label: PropTypes.node })
	),
	filterAriaLabel: PropTypes.string,
	controls: PropTypes.node,
	controlsPlacement: PropTypes.oneOf(["second-row", "first-row"]),
	isLoading: PropTypes.bool,
	error: PropTypes.any,
	errorMessage: PropTypes.node,
	onRetry: PropTypes.func,
	children: PropTypes.node,
};
